import { Button, Grid, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { get as _get } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createAutoService } from '../../../api/createServiceApi';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import {
  getAuthKey,
  getClaimNumber,
  getPartnerId,
  getServicingOpCo,
  getSessionId,
  getUrlKey,
  getZipCode,
} from '../../../app/store/combinedSelectors';
import * as ContextRouterActions from '../../../app/store/ContextRouter/ContextRouterActions';
import {
  clearCvpEligibility,
  updateSelectedProvidersState,
  updateStageState,
} from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { useActions } from '../../../app/store/useActions';
import { backBtn, ClaimServicesConstants } from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import getCopyById from '../../../copy/copyMapper';
import { CreateAutoServiceRequest } from '../../../model/create-auto-service-request';
import { ProviderInfo, SearchContext, StageNames } from '../../../types/type';
import { postMessageToParentApp } from '../../../utils/Utilities';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import ServiceError, { SERVICE_TYPE } from '../../shared/ServiceErrorScreen/ServiceError';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';
import Search from '../Search/Search';

export default function RepairServiceSelection() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const [serviceError, setServiceError] = React.useState(false);
  const claimSummary = useAppSelector((state) => state.claimDetail.claimSummary);
  const vin = useAppSelector((state) => _get(state, 'claimDetail.claimSummary.autoClaimDetails[0].vin', ''));
  const claimNumber = useSelector(getClaimNumber);
  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  let zipCode = useSelector(getZipCode);
  const partnerId = useSelector(getPartnerId);
  const opco = useSelector(getServicingOpCo);
  const copy = getCopyById(opco, partnerId);
  const theme = useTheme();

  // If the zip code is not present in validate call response, fallback to the one which is in claim details response
  if (!zipCode) {
    zipCode = claimSummary?.lossLocationAddress.postalCode;
  }
  const contextRouterActions: typeof ContextRouterActions = useActions(ContextRouterActions);

  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const currentStage = availableProgressStages.filter((stage) => stage.viewActive === true);

  async function handleSelection(selectedService: any) {
    // Make web service call to create a service
    const response = await selectService(selectedService);
    // Hide LoadingIndicator
    setModalLoadingOpen(false);
    if (_get(response, 'response.status.code', '') >= 200) {
      // Once success, update the local store
      updateLocalStore();

      // CVP is only available when there's no DRP shop selected, so a successful DRP
      // assignment always makes the claim CVP-ineligible from this point on. The Photos
      // step reads this value, so clear it now rather than leaving it stale from claim load.
      dispatch(clearCvpEligibility());

      // Set Provider Info
      const provider: ProviderInfo = {
        serviceTypeName: StageNames.REPAIR,
        serviceAvailable: 'available',
        providerName: selectedService.name,
        providerPhone: selectedService.contactMethodDetail.primaryPhoneNumber,
        providerAddress:
          selectedService.address.addressLine1 +
          ' ' +
          selectedService.address.city +
          ', ' +
          selectedService.address.state +
          ' ' +
          selectedService.address.zipCode,
      };

      // Go to the next screen
      gotoNextScreen(provider);
    } else {
      //Show UI for service error handling
      setServiceError(true);
    }
  }

  async function selectService(selectedService: any) {
    // Show LoadingIndicator
    setModalLoadingOpen(true);
    const postBody: CreateAutoServiceRequest = {
      claim: {
        claimNumber: claimNumber,
        vehicle: {
          vin: vin,
          serviceRequestInfo: {
            serviceRequestProviderId: selectedService.id,
            serviceRequestCode: ClaimServicesConstants.DRP,
            serviceRequestDescription: ClaimServicesConstants.DRP,
          },
        },
      },
    };
    const response = await createAutoService(urlKey, sessionId, authKey, postBody);
    if (_get(response, 'response.status.code', '') === 200) {
      if (_get(response, 'response.updatedAuthToken')) {
        contextRouterActions.updateAuthToken(response.response.updatedAuthToken);
      }
      return response;
    } else {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_AUTO_DRP_SHOP_SELECTION_FAIL_ERROR, '', response);
    }
  }

  function updateLocalStore() {
    // Update provider's information in local store too, since the remote update was success.
    const selectedProvider: ProviderInfo = {
      serviceTypeName: StageNames.REPAIR,
      serviceAvailable: '',
      providerName: '',
      providerID: '',
      providerPhone: '',
      providerUrl: undefined,
      subServiceTypes: undefined,
    };
    dispatch(
      updateSelectedProvidersState({
        selectedProvider: selectedProvider,
      })
    );
    //Update the current stage state and prepare the next stage state.
    const updatedStages = availableProgressStages.map((stage) => {
      stage =
        stage.id === currentStage[0].id
          ? {
              ...stage,
              completed: true,
              viewActive: true,
              showProgress: false,
              skipped: false,
            }
          : stage;
      return stage;
    });
    dispatch(updateStageState({ updatedStages: updatedStages }));
  }

  function gotoNextScreen(selectedProviderInfo: ProviderInfo) {
    navigate('/auto-service-confirmation', {
      state: {
        provider: selectedProviderInfo,
      },
    });
  }

  let content = <div></div>;
  if (!serviceError) {
    content = (
      <Box sx={{ width: '100%' }}>
        <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>
        <Search locationSelection={handleSelection} opcoId={''} copy={copy} searchContext={SearchContext.REPAIR} />

        {modalLoadingOpen && <ModalLinearActivityIndicator />}
        <Grid
          sx={{
            marginTop: '2em',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'flex-start',
            p: '1rem',
          }}
          item
          xs={12}
        >
          <Button
            sx={{
              ...theme.custom.styles.genericSecondaryButton,
            }}
            onClick={() => navigate(-1)}
          >
            {backBtn}
          </Button>
        </Grid>
      </Box>
    );
  } else {
    content = (
      <Box sx={{ width: '100%' }}>
        {/* Show Progress bar only if there is no service error */}
        {!serviceError && <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>}
        {serviceError && <ServiceError serviceType={SERVICE_TYPE.REPAIR_SHOP} />}
      </Box>
    );
  }
  return <Box sx={{ width: '100%' }}>{content}</Box>;
}
